/**
 * Public feedback submission page — accessed via invite token link.
 * No authentication required.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api, extractApiError } from '../api/client';
import StarRating from '../components/StarRating';
import type { FeedbackCategory, FeatureRequest } from '@shared/types';

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'ux', label: 'User Experience' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'pricing', label: 'Pricing Feedback' },
  { value: 'other', label: 'Other' },
];

interface TokenInfo {
  betaUser: { id: string; name: string };
  startup: { id: string; name: string; description: string };
}

export default function FeedbackFormPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [tokenError, setTokenError] = useState('');
  const [isLoadingToken, setIsLoadingToken] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    rating: 0,
    category: '' as FeedbackCategory | '',
    comment: '',
    featureRequests: [{ name: '', reason: '' }] as FeatureRequest[],
    wouldRecommend: '' as 'yes' | 'no' | 'maybe' | '',
    npsScore: -1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    const verifyToken = async () => {
      try {
        const { data } = await api.get(`/feedback/token/${token}`);
        setInfo(data.data);
      } catch (err) {
        const apiErr = extractApiError(err);
        setTokenError(apiErr.code === 'TOKEN_EXPIRED'
          ? 'This invite link has expired. Please ask the founder for a new one.'
          : 'Invalid invite link. Please check the URL and try again.'
        );
      } finally {
        setIsLoadingToken(false);
      }
    };
    verifyToken();
  }, [token]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (form.rating === 0) errs.rating = 'Please select a rating';
    if (!form.category) errs.category = 'Please select a category';
    if (!form.comment.trim()) errs.comment = 'Please provide feedback';
    else if (form.comment.length < 20) errs.comment = 'At least 20 characters required';
    if (!form.wouldRecommend) errs.wouldRecommend = 'Please answer this question';
    if (form.npsScore < 0) errs.npsScore = 'Please select a score';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.post('/feedback', {
        token,
        category: form.category,
        rating: form.rating,
        comment: form.comment,
        featureRequests: form.featureRequests.filter((f) => f.name.trim() && f.reason.trim()),
        wouldRecommend: form.wouldRecommend,
        npsScore: form.npsScore,
      });
      setSubmitted(true);
    } catch (err) {
      alert(extractApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f1a]">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f1a] px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-4xl mb-4">🔗</div>
          <h2 className="text-xl font-bold text-white mb-2">
            {tokenError.includes('expired') ? 'Link Expired' : 'Invalid Link'}
          </h2>
          <p className="text-gray-400 text-sm mb-4">{tokenError}</p>
          {tokenError.includes('expired') && (
            <p className="text-gray-500 text-xs">
              Contact the founder who sent you this link and ask them to generate a new invite from their beta dashboard.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f1a] px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-white mb-2">Thank you, {info?.betaUser.name}!</h2>
          <p className="text-gray-400">Your feedback on <strong className="text-white">{info?.startup.name}</strong> has been received. The team really appreciates your time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f1a] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-3xl">⚒️</span>
          <h1 className="text-2xl font-bold text-white mt-2">{info?.startup.name}</h1>
          <p className="text-gray-400 text-sm mt-1">Feedback for {info?.betaUser.name}</p>
          {info?.startup.description && (
            <p className="text-gray-500 text-xs mt-2 italic">{info.startup.description}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="card space-y-8">
          {/* Rating */}
          <div>
            <label className="label text-base">Overall Rating *</label>
            <StarRating value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} size="lg" />
            {errors.rating && <p className="error-text">{errors.rating}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="label">Category *</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setForm({ ...form, category: c.value })}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    form.category === c.value
                      ? 'bg-brand-500/20 border-brand-500 text-brand-500'
                      : 'bg-transparent border-[#2a2a3e] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {errors.category && <p className="error-text">{errors.category}</p>}
          </div>

          {/* Comment */}
          <div>
            <div className="flex justify-between mb-1">
              <label className="label mb-0">Detailed Feedback *</label>
              {/* FIX: #6 — live character counter with min-length coloring */}
              {form.comment.length > 0 && (
                <span className={`text-xs ${
                  form.comment.length >= 20 ? 'text-green-400' : 'text-orange-400'
                }`}>
                  {form.comment.length} / 20 characters minimum
                </span>
              )}
            </div>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              rows={5}
              placeholder="Share your honest experience. What worked? What didn't? What surprised you? (min. 20 characters)"
              className={`input-field resize-none ${errors.comment ? 'error' : ''}`}
            />
            {errors.comment && <p className="error-text">{errors.comment}</p>}
          </div>

          {/* Feature Requests */}
          <div>
            <label className="label">Feature Requests (up to 3)</label>
            <p className="text-xs text-gray-500 mb-3">What specific features would make this more valuable to you?</p>
            {form.featureRequests.slice(0, 3).map((fr, i) => (
              <div key={i} className="space-y-2 mb-3 bg-[#0f0f1a] rounded-xl p-4">
                <input
                  type="text"
                  value={fr.name}
                  onChange={(e) => {
                    const updated = [...form.featureRequests];
                    updated[i] = { ...fr, name: e.target.value };
                    setForm({ ...form, featureRequests: updated });
                  }}
                  placeholder="Feature name"
                  className="input-field text-sm"
                />
                <input
                  type="text"
                  value={fr.reason}
                  onChange={(e) => {
                    const updated = [...form.featureRequests];
                    updated[i] = { ...fr, reason: e.target.value };
                    setForm({ ...form, featureRequests: updated });
                  }}
                  placeholder="Why does this matter to you?"
                  className="input-field text-sm"
                />
              </div>
            ))}
            {form.featureRequests.length < 3 && (
              <button
                type="button"
                onClick={() => setForm({ ...form, featureRequests: [...form.featureRequests, { name: '', reason: '' }] })}
                className="text-sm text-brand-500 hover:text-brand-600"
              >
                + Add another feature request
              </button>
            )}
          </div>

          {/* Would Recommend */}
          <div>
            <label className="label">Would you recommend this to a colleague? *</label>
            <div className="flex gap-3">
              {(['yes', 'no', 'maybe'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm({ ...form, wouldRecommend: opt })}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.wouldRecommend === opt
                      ? 'bg-brand-500/20 border-brand-500 text-brand-500'
                      : 'border-[#2a2a3e] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {opt === 'yes' ? '👍 Yes' : opt === 'no' ? '👎 No' : '🤔 Maybe'}
                </button>
              ))}
            </div>
            {errors.wouldRecommend && <p className="error-text">{errors.wouldRecommend}</p>}
          </div>

          {/* NPS */}
          <div>
            <label className="label">How likely are you to recommend this to others? (0–10) *</label>
            <div className="flex gap-1 flex-wrap mt-2">
              {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, npsScore: n })}
                  className={`w-9 h-9 rounded-lg text-sm font-medium border transition-colors ${
                    form.npsScore === n
                      ? n >= 9 ? 'bg-green-500/20 border-green-500 text-green-400'
                        : n >= 7 ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                        : 'bg-red-500/20 border-red-500 text-red-400'
                      : 'border-[#2a2a3e] text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>0–6 Detractor</span>
              <span>7–8 Passive</span>
              <span>9–10 Promoter</span>
            </div>
            {errors.npsScore && <p className="error-text">{errors.npsScore}</p>}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3 text-base">
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
